import { useEffect, useState } from "react";
import { MCT_APP_MAIN_ID, MCT_GLOBAL_SKIP_NAV_ID } from "../config";
import { matchPath, type RouteComponentProps, useLocation } from "react-router";
import { useCounter } from "usehooks-ts";
import { landingPageRoutes } from "../app/routes";
import { useTypedTranslate } from "./intlHooks";

/**
 * An independent reference to a click event listener for the global skip nav, which
 * can be referenced within `updateGlobalSkipNav`, to be removed and then reassigned each
 * time the nav is updated
 */
let globalSkipNavClickListener: EventListener = () => {};

/**
 * Used on every route to update the `href` and `onClick` attributes of the global skip
 * nav, and optionally also the link's text. Also used internally by the `useGlobalSkipNav`
 * hook, to set a custom jump link id and optionally the skip nav's text and/or an extra
 * onClick callback, on a route-by-route basis
 */
export const updateGlobalSkipNav = ({
  skipNavEl,
  jumpToId = `${MCT_APP_MAIN_ID}`,
  location,
  onClick,
  textContent,
}: {
  skipNavEl: HTMLAnchorElement;
  jumpToId?: string;
  location: RouteComponentProps["location"];
  onClick?: () => void;
  textContent?: string;
}) => {
  const jumpToElement: HTMLElement | null = document.getElementById(jumpToId);
  // Ensure jump element is focusable
  jumpToElement?.setAttribute("tabindex", "-1");

  if (textContent) {
    skipNavEl.textContent = textContent;
  }

  // Set the `href` to track that the fragment id is set correctly. This value
  // is not used directly, because with hash-based routing, we end up with 2 hashes (a
  // malformed URL).
  // @see `newUrl`, below.
  // This is for show, and the click listener is what actually makes the skip nav
  // function correcdtly.
  skipNavEl.setAttribute("href", `#${jumpToId}`);
  // Used to update the URL programmatically without reloading the page
  // The assembled URL will have two hashes, one for the hash-based route and one
  // for the updated fragment id
  const newUrl = `${window.location.origin}${window.location.pathname}#${location.pathname}${location.search}#${jumpToId}`;

  // Remove the existing click listener each time the function is run
  skipNavEl.removeEventListener("click", globalSkipNavClickListener);

  // Set up a new "click" listener
  // This listener is what actually controls the skip nav's behavior. It's necessary
  // because we use hashed-based routing. Just allowing the link's native behavior
  // with an `href` value like `"#the-fragment-id" will result in the page reloading
  // and navigating to the landing page (because that fragment is not a valid route)
  globalSkipNavClickListener = e => {
    e.preventDefault();
    if (onClick) {
      onClick();
    }
    window.location.href = `${newUrl}`;
    jumpToElement?.focus();
  };

  // Assign the updated `globalSkipNavClickListener`
  skipNavEl.addEventListener("click", globalSkipNavClickListener);
};

/**
 * Hook for a one-off customization of the global skip-nav
 *
 * @see `public/index.html` (where the nav is added to HTML)
 * @see `MaxGlobalHeader` (where the nav is updated on every route change)
 */
export const useGlobalSkipNav = ({
  textContent,
  jumpToId,
  onClick,
}: {
  /** Alternate content for the skip nav link */
  textContent?: string;
  /** Fragment for the skip nav to jump to, should refernce an element `id` in the page */
  jumpToId: string;
  /** Any extra things that should happen when the skip nav link is clicked. Helpful if memoized */
  onClick?: () => void;
}) => {
  const [globalSkipNavEl, setGlobalSkipNavEl] =
    useState<HTMLAnchorElement | null>(null);
  const location = useLocation();
  const t = useTypedTranslate();
  const { count: skipNavAttributesUpdated, increment } = useCounter(0);

  /**
   * Set a reference to the global skip nav (in the DOM) in hook state
   * This reference is used by other effects without continually querying the DOM
   */
  useEffect(() => {
    if (globalSkipNavEl) {
      return;
    }
    const skipNavEl = document.getElementById(`${MCT_GLOBAL_SKIP_NAV_ID}`);
    if (skipNavEl) {
      setGlobalSkipNavEl(skipNavEl as HTMLAnchorElement);
    }
  }, [globalSkipNavEl]);

  /**
   * Ensure that overrides passed into `useSkipNav` are used by the the global skip
   * nav, by watching for changes to a data attribute that holds the desired override id.
   * If it's set to something other than what's passed into the hook, hook state
   * will be updated and the nav's update helper run again
   */
  useEffect(() => {
    if (!globalSkipNavEl) {
      return;
    }
    let mo: MutationObserver | undefined = undefined;
    const observerCb: MutationCallback = mutations => {
      mutations.forEach(mutationRecord => {
        if (mutationRecord.attributeName === "href") {
          const fragmentId = globalSkipNavEl
            .getAttribute("href")
            ?.replace("#", "");
          // If the fragment isn't what was passed into `useSkipNav`, increment
          // a value in hook state to trigger a new update to the nav
          if (jumpToId !== fragmentId) {
            increment();
          }
        }
      });
    };
    mo = new MutationObserver(observerCb);
    mo.observe(globalSkipNavEl, {
      attributeFilter: ["href"],
    });
    return () => {
      mo?.disconnect();
    };
  }, [globalSkipNavEl, increment, jumpToId]);

  /**
   * Effect that actually calls the function to update values and the event listener
   * for the global skip nav
   */
  useEffect(() => {
    if (!globalSkipNavEl) {
      return;
    }
    updateGlobalSkipNav({
      skipNavEl: globalSkipNavEl,
      jumpToId,
      location,
      onClick,
      textContent: textContent ?? t("skip_to_content"),
    });
  }, [
    globalSkipNavEl,
    jumpToId,
    location,
    onClick,
    skipNavAttributesUpdated,
    t,
    textContent,
  ]);
};

/**
 * Checks whether the current route is a landing page.
 *
 * Landing page routes are places a user could logically land from a link outside
 * MPF, and and focus should not be moved to any internal elements on these routes
 */
export const useIsLandingPageRoute = (): boolean => {
  const { pathname } = useLocation();
  return [...landingPageRoutes].some(
    route => !!matchPath(pathname, { path: route, exact: true, strict: true })
  );
};
